import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, TextField, MenuItem, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Checkbox, CircularProgress, Alert, IconButton, Breadcrumbs, Link,
  InputAdornment, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Save as SaveIcon, 
  ArrowBack as BackIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import tripService from '../services/tripService';
import cityService from '../services/cityService';
import routeService from '../services/routeService';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const WEEKDAY_CODES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_LABELS = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};

// 'YYYY-MM-DD' -> código de día ('monday'...), interpretando la fecha en hora local.
const getDayCode = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.split('-').map(Number);
  return WEEKDAY_CODES[new Date(year, month - 1, day).getDay()];
};

const getTodayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const getRouteStopsLabel = (route) =>
  [...(route?.route_cities || [])]
    .sort((a, b) => a.order - b.order)
    .map((rc) => rc.city_name)
    .join(' → ');

const TripCreatePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [mode, setMode] = useState('route');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  // Mientras el usuario no elija una ruta a mano, se propone la del día de la fecha.
  const [routeTouched, setRouteTouched] = useState(false);
  const [date, setDate] = useState(getTodayIso());
  const [notes, setNotes] = useState('');
  
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCities();
    loadRoutes();
  }, []);

  useEffect(() => {
    if (routeTouched) return;
    const dayCode = getDayCode(date);
    const routeOfDay = routes.find((route) => route.day_of_week === dayCode);
    setSelectedRouteId(routeOfDay ? routeOfDay.id : '');
  }, [date, routes, routeTouched]);

  useEffect(() => {
    if (mode === 'route' && selectedRouteId) {
      loadOrders({ route: selectedRouteId });
    } else if (mode === 'city' && selectedCity) {
      loadOrders({ city: selectedCity });
    } else {
      setOrders([]);
      setSelectedOrders([]);
    }
  }, [mode, selectedCity, selectedRouteId]);

  const selectedRoute = routes.find((route) => route.id === selectedRouteId);
  const hasDestination = mode === 'route' ? Boolean(selectedRoute) : Boolean(selectedCity);
  const destinationLabel = mode === 'route' ? selectedRoute?.name : selectedCity;

  const loadCities = async () => {
    try {
      setLoadingCities(true);
      const data = await cityService.getCities({
        paginate: 'false',
        is_available_for_trips: 'true',
        active_only: 'true'
      });
      setCities(data.results || data);
    } catch (err) {
      setError('Error al cargar las ciudades disponibles.');
    } finally {
      setLoadingCities(false);
    }
  };

  const loadRoutes = async () => {
    try {
      setLoadingRoutes(true);
      const data = await routeService.getRoutes();
      setRoutes(data.results || data);
    } catch (err) {
      setError('Error al cargar las rutas de reparto.');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const loadOrders = async (filters) => {
    try {
      setLoadingOrders(true);
      setError('');
      const data = filters.route
        ? await tripService.getPendingOrdersByRoute(filters.route)
        : await tripService.getPendingOrdersByCity(filters.city);
      setOrders(data);
      // Seleccionar todos por defecto
      setSelectedOrders(data.map(o => o.id));
    } catch (err) {
      setError(filters.route
        ? 'Error al cargar los pedidos de la ruta seleccionada.'
        : 'Error al cargar los pedidos de la ciudad seleccionada.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleToggleOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleCreateTrip = async () => {
    if (!hasDestination) {
      setError(mode === 'route' ? 'Debe seleccionar una ruta.' : 'Debe seleccionar una ciudad.');
      return;
    }
    if (selectedOrders.length === 0) {
      setError('Debe seleccionar al menos un pedido para el viaje.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const newTrip = await tripService.createTrip({
        ...(mode === 'route' ? { route_id: selectedRouteId } : { city: selectedCity }),
        date,
        notes,
        order_ids: selectedOrders
      });
      navigate(`/trips/${newTrip.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al crear el viaje. Intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/trips'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Viajes
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Armar Nuevo Viaje
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
          <IconButton onClick={() => navigate('/trips')} sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', color: '#0e141b' }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#0e141b', 
                fontSize: { xs: '1.875rem', md: '2.25rem' }, 
                fontWeight: 900, 
                mb: 0.5,
                letterSpacing: '-0.033em'
              }}
            >
              Nuevo Viaje
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Selecciona una ruta (o una ciudad) y los pedidos confirmados para organizar la logística.
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '0.5rem' }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 4 }}>
          {/* Form Side */}
          <Box>
            <Paper sx={{ p: 3, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#0e141b' }}>Configuración del Viaje</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={mode}
                  onChange={(e, value) => value && setMode(value)}
                  sx={{
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 },
                    '& .Mui-selected': { bgcolor: '#80002014 !important', color: '#800020 !important' }
                  }}
                >
                  <ToggleButton value="route">Por ruta</ToggleButton>
                  <ToggleButton value="city">Por ciudad</ToggleButton>
                </ToggleButtonGroup>

                {mode === 'route' ? (
                  <TextField
                    select
                    label="Ruta"
                    value={selectedRouteId}
                    onChange={(e) => {
                      setRouteTouched(true);
                      setSelectedRouteId(e.target.value);
                    }}
                    disabled={loadingRoutes}
                    fullWidth
                    helperText={
                      selectedRoute
                        ? getRouteStopsLabel(selectedRoute) || 'La ruta no tiene ciudades'
                        : !routeTouched && !loadingRoutes && routes.length > 0
                          ? `No hay una ruta configurada para el ${DAY_LABELS[getDayCode(date)]?.toLowerCase() || 'día elegido'}.`
                          : ' '
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RouteIcon sx={{ color: '#4e7397' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                    }}
                  >
                    {routes.map((route) => (
                      <MenuItem key={route.id} value={route.id}>
                        {route.name}{route.day_of_week ? ` · ${DAY_LABELS[route.day_of_week]}` : ''}
                      </MenuItem>
                    ))}
                    {routes.length === 0 && !loadingRoutes && (
                      <MenuItem value="" disabled>No hay rutas configuradas</MenuItem>
                    )}
                  </TextField>
                ) : (
                  <TextField
                    select
                    label="Ciudad"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    disabled={loadingCities}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RouteIcon sx={{ color: '#4e7397' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.name}>{city.name}</MenuItem>
                    ))}
                    {cities.length === 0 && !loadingCities && (
                      <MenuItem value="" disabled>No hay ciudades configuradas</MenuItem>
                    )}
                  </TextField>
                )}

                <TextField
                  type="date"
                  label="Fecha Programada"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                  }}
                />

                <TextField
                  label="Notas para el Repartidor"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Ej: Cobrar en efectivo, cliente no atiende de 12 a 14..."
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' }
                  }}
                />

                <Box sx={{ pt: 2 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleCreateTrip}
                    disabled={saving || selectedOrders.length === 0 || !hasDestination}
                    sx={{ 
                      bgcolor: '#800020', 
                      '&:hover': { bgcolor: '#600010' },
                      borderRadius: '0.5rem',
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none'
                    }}
                  >
                    {saving ? 'Generar Viaje...' : 'Confirmar y Generar Viaje'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Orders Side */}
          <Box>
            {hasDestination ? (
              <Paper sx={{ borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #d0dbe7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0e141b' }}>
                    Pedidos en {destinationLabel}
                  </Typography>
                  <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                    <span style={{ fontWeight: 700, color: '#0e141b' }}>{selectedOrders.length}</span> seleccionados
                  </Typography>
                </Box>

                {loadingOrders ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 8 }}>
                    <CircularProgress sx={{ color: '#800020', mb: 2 }} />
                    <Typography sx={{ color: '#4e7397' }}>Cargando pedidos disponibles...</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: '#f6f7f8' }}>
                            <Checkbox
                              checked={selectedOrders.length === orders.length && orders.length > 0}
                              indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length}
                              onChange={(e) => setSelectedOrders(e.target.checked ? orders.map(o => o.id) : [])}
                              sx={{ color: '#d0dbe7', '&.Mui-checked': { color: '#800020' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Pedido</TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Cliente</TableCell>
                          <TableCell sx={{ bgcolor: '#f6f7f8', color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Peso</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                              <Typography sx={{ color: '#4e7397' }}>{mode === 'route' ? 'No hay pedidos confirmados en las ciudades de esta ruta.' : 'No hay pedidos confirmados para esta ciudad.'}</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          orders.map((order, index) => (
                            <React.Fragment key={order.id}>
                            {order.route_stop && order.route_stop.order !== orders[index - 1]?.route_stop?.order && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ bgcolor: '#f6f7f8', py: 1 }}>
                                  <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.8125rem' }}>
                                    Parada {order.route_stop.order} · {order.route_stop.city_name}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow hover onClick={() => handleToggleOrder(order.id)} sx={{ cursor: 'pointer' }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedOrders.includes(order.id)}
                                  onChange={() => handleToggleOrder(order.id)}
                                  sx={{ color: '#d0dbe7', '&.Mui-checked': { color: '#800020' } }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>#{order.order_number}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.875rem' }}>{order.client?.name}</Typography>
                                <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>{order.client?.delivery_address}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, color: '#1b5e20' }}>{order.total_weight} kg</Typography>
                              </TableCell>
                            </TableRow>
                            </React.Fragment>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, border: '2px dashed #d0dbe7', borderRadius: '1rem' }}>
                <RouteIcon sx={{ fontSize: 48, color: '#d0dbe7', mb: 2 }} />
                <Typography sx={{ color: '#4e7397', textAlign: 'center' }}>{mode === 'route' ? 'Selecciona una ruta para ver los pedidos de todas sus ciudades.' : 'Selecciona una ciudad para ver los pedidos disponibles para el viaje.'}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TripCreatePage;
